import { useRef, useState, useEffect, useMemo } from 'react';
import type { Attachment } from '../types';
import playIcon from "../assets/icon-video-play.svg";
import pauseIcon from "../assets/icon-video-pause.svg";
import {
  MediaController,
  MediaControlBar,
  MediaTimeRange,
  MediaTimeDisplay,
  MediaPlayButton,
  MediaMuteButton,
  MediaFullscreenButton,
} from "media-chrome/react";

interface VideoPlayerProps {
  attachment: Attachment;
}

const getVideoErrorDetails = (mediaError: MediaError | null) => {
  if (!mediaError) {
    return {
      type: "UNKNOWN",
      message: "播放视频时发生未知错误",
    };
  }

  switch (mediaError.code) {
    case MediaError.MEDIA_ERR_ABORTED:
      return {
        type: "ABORTED",
        message: "视频加载被中止",
      };
    case MediaError.MEDIA_ERR_NETWORK:
      return {
        type: "NETWORK",
        message: "加载视频时发生网络错误",
      };
    case MediaError.MEDIA_ERR_DECODE:
      return {
        type: "DECODE",
        message: "视频解码失败，可能是由于不支持的格式",
      };
    case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
      return {
        type: "NOT_SUPPORTED",
        message: "视频格式不受支持或文件损坏",
      };
    default:
      return {
        type: "UNKNOWN",
        message: "播放视频时发生未知错误",
      };
  }
};

export default function VideoPlayer({ attachment }: VideoPlayerProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [hasStartedPlaying, setHasStartedPlaying] = useState<boolean>(false);
  const [error, setError] = useState<{ type: string; message: string } | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Reset when source changes
  useEffect(() => {
    setHasStartedPlaying(false);
    setError(null);
    setLoading(false);

    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
      video.load();
    }
  }, [attachment.url]);

  const handleLoadStart = () => {
    setLoading(true);
    setError(null);
  };

  const handleLoadedMetadata = () => {
    setLoading(false);
    setError(null);
  };

  const handleCanPlay = () => {
    setLoading(false);
  };

  const handleWaiting = () => {
    setLoading(true);
  };

  const handlePlaying = () => {
    setHasStartedPlaying(true);
    setLoading(false);
  };

  const handleVideoError = (event: React.SyntheticEvent<HTMLVideoElement>) => {
    const details = getVideoErrorDetails(event.currentTarget.error);
    setLoading(false);
    setError(details);

    console.error("Video playback error:", {
      code: event.currentTarget.error?.code,
      message: event.currentTarget.error?.message,
      url: attachment.url,
    });
  };

  const handleRetry = () => {
    setError(null);
    setHasStartedPlaying(false);
    setLoading(false);

    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
      video.load();
    }
  };

  const posterUrl = useMemo((): string | undefined => {
    if (error) return undefined;
    return `${attachment.url}?x-oss-process=video/snapshot,t_0,f_jpg,w_0,h_0,ar_auto,q_80`;
  }, [attachment.url, error]);

  return (
    <div className='w-full max-w-lg mx-auto px-8 mb-12 aspect-video'>
      {error ? (
        <div className="w-full h-full bg-black/70 flex flex-col items-center justify-center p-6 text-center rounded-2xl">
          <h3 className="mb-2 text-base font-semibold text-[#E63232]">
            视频加载失败
          </h3>
          <p className="mb-4 text-sm text-white max-w-xs">
            {error.message}
          </p>
          <button
            type="button"
            onClick={handleRetry}
            className="inline-flex items-center justify-center rounded-full bg-white/90 px-6 py-2 text-sm font-semibold text-[#1B1B1B] transition hover:bg-white"
          >
            重新加载
          </button>
        </div>
      ) : (
        <div className='relative h-full w-full overflow-hidden bg-black rounded-2xl'>
          {loading && (
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/50 border-t-white"></div>
            </div>
          )}

          <MediaController className='z-0 w-full h-full rounded-2xl overflow-hidden' autohide="1.5">
            <video
              slot="media"
              id='video'
              ref={videoRef}
              src={attachment.url}
              controls={false}
              playsInline
              webkit-playsinline="true"
              x5-playsinline="true"
              preload="auto"
              poster={posterUrl ?? undefined}
              className="w-full h-full object-contain z-0"
              onLoadStart={handleLoadStart}
              onLoadedMetadata={handleLoadedMetadata}
              onCanPlay={handleCanPlay}
              onWaiting={handleWaiting}
              onPlaying={handlePlaying}
              onError={handleVideoError}
            >
              您的浏览器不支持 video 元素。
            </video>

            {/* 中间播放按钮 */}
            {!loading && (
              <MediaPlayButton
                slot="centered-chrome"
                noTooltip={true}
                style={{
                  width: "60px",
                  height: "60px",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  padding: "0",
                  borderRadius: "50%",
                  border: "none"
                }}
              >
                <img slot="play" src={playIcon} alt="play" className='w-10 h-10 object-contain' />
                <img slot="pause" src={pauseIcon} alt="pause" className='w-8 h-8 object-contain' />
              </MediaPlayButton>
            )}

            {hasStartedPlaying && (
              <MediaControlBar>
                <MediaMuteButton noTooltip={true} style={{ width: "40px", padding: "5px 10px" }} />
                <MediaTimeRange style={{ "--media-range-padding": "5px" } as React.CSSProperties} />
                <MediaTimeDisplay style={{ padding: "5px" }} showDuration />
                <MediaFullscreenButton noTooltip={true} style={{ width: "40px", padding: "5px 8px" }} />
              </MediaControlBar>
            )}
          </MediaController>
        </div>
      )}
    </div>

  );
}
